import * as React from 'react';
import { IGradientStop } from "@longline/aqua-ui/Types";
import { ELayerType } from './ELayerType';
import { PresetGradients } from './PresetGradients';

interface ILayerTypeDefinition {
  /** Layer name */
  name: string;
  /** Layer unit */
  unit: React.ReactNode;
  /** Number of decimals to print in values. */
  decimals: number;
  /** Layer zIndex. Guarantees that bathymetry is under everything else. */
  zIndex: number;
  /** Available gradients */
  gradients: IGradientStop[][];
  /** (Interpolation layers): contour line frequency. 0 = no contour lines. */
  contours?: number;
  /** (Interpolation layers): contour line thickness */
  contourThickness?: number;
  /** (Current velocity layers): Particle trail length */
  trail?: number;
  /** (Current velocity layers): Particle density */
  density?: number;
  /** (Current velocity layers): Particle point size */
  pointSize?: number;
  /** (Current velocity layers): Particle animtion delay factor (0 = no delay) */
  delay?: number;  
}

const DefaultLayerDefinitions: Record<ELayerType, ILayerTypeDefinition> = {
  [ELayerType.Depth]: {
    name: 'Bathymetry',
    unit: 'm',
    decimals: 2,
    zIndex: 0,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.BlueSolid, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.CurrentSpeed]: {
    name: 'Current velocity',
    unit: 'm s⁻¹',
    decimals: 4,
    zIndex: 100,
    contours: 8,
    contourThickness: 1,
    pointSize: 2,
    gradients: [
      PresetGradients.CurrentBlue, PresetGradients.CurrentGrey
    ]
  },
  [ELayerType.DailyPOC]: {
    name: 'Daily POC Load',
    unit: 'gC m⁻² d⁻¹',
    decimals: 4,
    zIndex: 10,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Rainbow, PresetGradients.Grey, PresetGradients.Red, PresetGradients.Organix
    ]
  },
  [ELayerType.DailyPOM]: {
    name: 'Daily POM Load',
    unit: 'g DW m⁻² d⁻¹',
    decimals: 4,
    zIndex: 10,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Rainbow, PresetGradients.Grey, PresetGradients.Red, PresetGradients.Organix
    ]
  },
  [ELayerType.YearlyPOC]: {
    name: 'Yearly POC Load',
    unit: 'gC m⁻² y⁻¹',
    decimals: 4,
    zIndex: 10,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Rainbow, PresetGradients.Grey, PresetGradients.Red, PresetGradients.Organix
    ]
  },
  [ELayerType.Oxygen]: {
    name: 'Oxygen',
    unit: 'mg l⁻¹',
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Red, PresetGradients.Grey, PresetGradients.Mimosa, PresetGradients.Rainbow, PresetGradients.Organix
    ]
  },
  [ELayerType.Ammonia]: {
    name: 'Ammonia',
    unit: 'µmol l⁻¹',
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Ammonia, PresetGradients.Green, PresetGradients.Grey, PresetGradients.MelloYellow, PresetGradients.Rainbow, PresetGradients.Organix
    ]
  },
  [ELayerType.Chlorophyll]: {
    name: 'Chlorophyll',
    unit: 'µg l⁻¹',
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow, PresetGradients.Organix
    ]
  },
  [ELayerType.Pathogen]: {
    name: 'Pathogen',
    unit: '%',
    decimals: 2,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Mimosa, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow, PresetGradients.GreyStepped, PresetGradients.Organix
    ]
  },
  [ELayerType.PocInSediment]: {
    name: 'POC in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow, PresetGradients.Organix
    ]
  },
  [ELayerType.PomInSediment]: {
    name: 'POM in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow, PresetGradients.Organix
    ]
  },
  [ELayerType.BacteriaInSediment]: {
    name: 'Bacteria in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.OxygenInSediment]: {
    name: 'Oxygen in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.SulphateInSediment]: {
    name: 'Sulphate in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.IronHydroxideInSediment]: {
    name: 'Iron hydroxide in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.TotalSulphideInSediment]: {
    name: 'Total sulphide in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.Organix, PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.IronInSediment]: {
    name: 'Iron in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.IronSulphideInsediment]: {
    name: 'Iron sulphide in sediment',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  },
  [ELayerType.Therapeutants]: {
    name: 'Therapeutants',
    unit: null,
    decimals: 4,
    zIndex: 5,
    contours: 8,
    contourThickness: 1,
    gradients: [
      PresetGradients.RetroWagon, PresetGradients.Green, PresetGradients.Grey, PresetGradients.Rainbow
    ]
  }
}

export { ILayerTypeDefinition, DefaultLayerDefinitions, PresetGradients }

